import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { useQueryClient } from "@tanstack/react-query";
import Modal from "@/components/ui/modal";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  FileText,
  Printer,
  Download,
  AlertCircle,
  Loader2,
  RefreshCw,
  Trash2,
  FileDown,
} from "lucide-react";
import apiClient from "@/api/apiClient";
import { API_ENDPOINTS } from "@/api/header";
import { toast } from "sonner";
import { isStoreChargingTax } from "@/lib/utils";

const round2 = (v) => Math.round((v + Number.EPSILON) * 100) / 100;

const PosCreateQuotationModal = ({
  show,
  onHide,
  currentProducts = [],
  customer = null,
  currencySymbol = "$",
  onClearCart,
}) => {
  const currentStore = useSelector((state) => state.current_store);
  const currentUser = useSelector((state) => state.user_data);
  const queryClient = useQueryClient();

  // Form states
  const [quotationDate, setQuotationDate] = useState(() =>
    new Date().toISOString().split("T")[0],
  );
  const [validUntil, setValidUntil] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 14);
    return d.toISOString().split("T")[0];
  });
  const [status, setStatus] = useState("draft");
  const [notes, setNotes] = useState("");
  const [overallDiscountType, setOverallDiscountType] = useState("fixed");
  const [overallDiscountValue, setOverallDiscountValue] = useState(0);

  // Working copy of items to allow adjusting quantities/notes in quotation
  const [items, setItems] = useState([]);

  // PDF generation & result states
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createdQuotation, setCreatedQuotation] = useState(null);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [showPdfView, setShowPdfView] = useState(false);

  useEffect(() => {
    if (show) {
      setQuotationDate(new Date().toISOString().split("T")[0]);
      const d = new Date();
      d.setDate(d.getDate() + 14);
      setValidUntil(d.toISOString().split("T")[0]);
      setStatus("draft");
      setNotes("Quotation valid for 14 days. Prices subject to stock availability.");
      setOverallDiscountType("fixed");
      setOverallDiscountValue(0);
      setCreatedQuotation(null);
      setPdfBlobUrl(null);
      setShowPdfView(false);

      // Clone products from cart
      if (currentProducts && currentProducts.length > 0) {
        const cloned = currentProducts.map((p) => {
          let prodData = p.product_data;
          if (typeof prodData === "string") {
            try {
              prodData = JSON.parse(prodData);
            } catch {
              prodData = {};
            }
          }
          const unitPrice = parseFloat(p.unit_price || prodData?.sell_price || 0);
          const qty = parseFloat(p.quantity || 1);
          const disc = parseFloat(p.discount_amount || 0);
          const tax = parseFloat(p.tax_amount || 0);
          const total = parseFloat(p.line_total || unitPrice * qty);

          // Discount type: in POS cart it defaults to "none". For quotation API it must be "percentage" or "fixed"
          const discType =
            p.discount_type === "percentage" ? "percentage" : "fixed";

          return {
            product_id: p.product_id || null,
            name: prodData?.name || p.name || p.item_name || "Item",
            sku: prodData?.sku || p.sku || p.item_sku || "",
            quantity: qty,
            unit_price: unitPrice,
            original_price: parseFloat(p.original_price || unitPrice),
            discount_amount: disc,
            discount_type: discType,
            discount_value: parseFloat(p.discount_value || 0),
            tax_amount: tax,
            tax_percentage: parseFloat(
              p.tax_percentage ?? currentStore?.tax_percentage ?? 0,
            ),
            tax_class: p.tax_class || prodData?.tax_class || null,
            line_total: total,
            notes: p.notes || "",
            is_new_item: Boolean(p.is_new_item || !p.product_id),
            item_description: p.item_description || p.notes || "",
            product: prodData || p.product || null,
          };
        });
        setItems(cloned);
      } else {
        setItems([]);
      }
    }
  }, [show, currentProducts, currentStore]);

  const handleSetQuickDays = (days) => {
    const d = new Date();
    d.setDate(d.getDate() + days);
    setValidUntil(d.toISOString().split("T")[0]);
  };

  const handleUpdateItemQty = (index, newQty) => {
    const val = parseFloat(newQty);
    if (isNaN(val) || val <= 0) return;

    setItems((prev) =>
      prev.map((item, idx) => {
        if (idx !== index) return item;
        const base = round2(val * item.unit_price);
        const disc =
          item.discount_type === "percentage"
            ? round2((base * item.discount_value) / 100)
            : Math.min(base, item.discount_value || 0);
        const discounted = round2(base - disc);

        // recalculate tax if store charges tax
        let tax = 0;
        if (
          isStoreChargingTax(currentStore) &&
          item.product?.is_taxable &&
          item.product?.tax_method !== "inclusive"
        ) {
          const rate = parseFloat(currentStore.tax_percentage || 0);
          tax = round2((discounted * rate) / 100);
        }

        return {
          ...item,
          quantity: val,
          discount_amount: disc,
          tax_amount: tax,
          line_total: discounted,
        };
      }),
    );
  };

  const handleRemoveItem = (index) => {
    setItems((prev) => prev.filter((_, idx) => idx !== index));
  };

  // Calculate totals
  const totals = useMemo(() => {
    const itemsSubtotal = items.reduce(
      (sum, i) => sum + round2(i.quantity * i.unit_price),
      0,
    );
    const itemsDiscount = items.reduce(
      (sum, i) => sum + parseFloat(i.discount_amount || 0),
      0,
    );
    const itemsTax = items.reduce(
      (sum, i) => sum + parseFloat(i.tax_amount || 0),
      0,
    );

    // Apply overall quotation discount
    let extraDiscount = 0;
    const odVal = parseFloat(overallDiscountValue || 0);
    if (overallDiscountType === "percentage" && odVal > 0) {
      extraDiscount = round2(((itemsSubtotal - itemsDiscount) * odVal) / 100);
    } else if (overallDiscountType === "fixed" && odVal > 0) {
      extraDiscount = round2(odVal);
    }

    const totalDiscount = round2(itemsDiscount + extraDiscount);
    const grandTotal = Math.max(
      0,
      round2(itemsSubtotal - totalDiscount + itemsTax),
    );

    return {
      subtotal: round2(itemsSubtotal),
      discount: totalDiscount,
      tax: round2(itemsTax),
      total: grandTotal,
      itemCount: items.reduce((sum, i) => sum + parseFloat(i.quantity || 1), 0),
    };
  }, [items, overallDiscountType, overallDiscountValue]);

  // Generate PDF for created quotation
  const generateQuotationPdf = async (quotationId) => {
    setIsGeneratingPdf(true);
    try {
      if (pdfBlobUrl) {
        URL.revokeObjectURL(pdfBlobUrl);
        setPdfBlobUrl(null);
      }

      const res = await apiClient.get(
        API_ENDPOINTS.REPORTS.QUOTATION_PDF(quotationId),
        {
          responseType: "blob",
          timeout: 30000,
        },
      );

      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      setPdfBlobUrl(url);
      setShowPdfView(true);
    } catch (err) {
      console.error("Quotation PDF error:", err);
      toast.error(err.response?.data?.message || "Failed to generate Quotation PDF");
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const handlePrintPdf = () => {
    if (pdfBlobUrl) {
      const win = window.open(pdfBlobUrl, "_blank");
      if (win) {
        win.onload = () => win.print();
      }
    }
  };

  const handleDownloadPdf = () => {
    if (pdfBlobUrl && createdQuotation) {
      const link = document.createElement("a");
      link.href = pdfBlobUrl;
      link.download = `Quotation-${createdQuotation.quotation_number || createdQuotation.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleSubmit = async (andViewPdf = true, overrideStatus = null) => {
    const storeId =
      currentStore?.id ||
      (typeof currentStore === "string" ? currentStore : null);
    const userId =
      currentUser?.id ||
      currentUser?.user_id ||
      currentUser?.user?.id ||
      currentUser?.employee?.user_id ||
      null;

    if (!storeId) {
      toast.error("Active store is required to create quotation");
      return;
    }
    if (!userId) {
      toast.error("Active user is required to create quotation");
      return;
    }
    if (items.length === 0) {
      toast.error("Please add at least one product to the quotation");
      return;
    }

    setIsSubmitting(true);
    try {
      const finalStatus =
        overrideStatus ||
        (["draft", "sent", "accepted", "rejected", "expired"].includes(status)
          ? status
          : "draft");

      const payload = {
        customer_id: customer?.id ? String(customer.id) : null,
        store_id: storeId,
        user_id: userId,
        valid_until: validUntil
          ? new Date(validUntil).toISOString()
          : null,
        quotation_date: quotationDate
          ? new Date(quotationDate).toISOString()
          : new Date().toISOString(),
        subtotal: round2(totals.subtotal),
        tax_amount: round2(totals.tax),
        discount_amount: round2(totals.discount),
        discount_type:
          overallDiscountType === "percentage" ? "percentage" : "fixed",
        discount_value: round2(parseFloat(overallDiscountValue || 0)),
        total: round2(totals.total),
        item_count: Math.round(items.length),
        status: finalStatus,
        notes: notes ? String(notes).trim() : null,
        custom_fields: null,
        items: items.map((i) => {
          const unitPrice = round2(parseFloat(i.unit_price || 0));
          const origPrice = round2(
            parseFloat(i.original_price ?? unitPrice),
          );
          const qty = Math.max(0.0001, parseFloat(i.quantity || 1));
          const discAmt = round2(parseFloat(i.discount_amount || 0));
          const discVal = round2(parseFloat(i.discount_value || 0));
          const discType =
            i.discount_type === "percentage" ? "percentage" : "fixed";

          const taxAmt = round2(parseFloat(i.tax_amount || 0));
          const taxPct = round2(
            parseFloat(
              i.tax_percentage ?? currentStore?.tax_percentage ?? 0,
            ),
          );
          const lineTot = round2(parseFloat(i.line_total || 0));

          let productDataArr = null;
          if (i.product) {
            try {
              productDataArr = [
                typeof i.product === "object"
                  ? JSON.stringify(i.product)
                  : String(i.product),
              ];
            } catch {
              productDataArr = null;
            }
          }

          return {
            product_id: i.product_id ? String(i.product_id) : null,
            store_id: storeId,
            is_new_item: Boolean(i.is_new_item || !i.product_id),
            item_name: String(i.name || i.item_name || "Item").slice(0, 255),
            item_description: i.item_description || i.notes || null,
            item_sku:
              i.sku || i.item_sku
                ? String(i.sku || i.item_sku).slice(0, 100)
                : null,
            quantity: qty,
            unit_price: unitPrice,
            original_price: origPrice,
            discount_amount: discAmt,
            discount_type: discType,
            discount_value: discVal,
            tax_amount: taxAmt,
            tax_class: i.tax_class || i.product?.tax_class || null,
            tax_percentage: taxPct,
            line_total: lineTot,
            notes: i.notes ? String(i.notes) : null,
            product_data: productDataArr,
            custom_fields: null,
          };
        }),
      };

      const res = await apiClient.post(
        API_ENDPOINTS.QUOTATIONS.QUOTATIONS,
        payload,
      );

      const savedQuotation = res.data?.data || res.data;
      setCreatedQuotation(savedQuotation);
      toast.success(
        `Quotation #${savedQuotation.quotation_number || savedQuotation.id} created successfully!`,
      );

      try {
        await queryClient.invalidateQueries({
          queryKey: ["quotations"],
          refetchType: "all",
        });
      } catch {
        // ignore
      }

      if (andViewPdf && savedQuotation.id) {
        await generateQuotationPdf(savedQuotation.id);
      } else {
        onHide();
      }
    } catch (err) {
      console.error("Create quotation error:", err);
      toast.error(
        err.response?.data?.message || "Failed to create quotation from POS",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      open={show}
      onOpenChange={(v) => !v && onHide()}
      title={
        <div className="flex items-center gap-2">
          <FileText className="h-5 w-5 text-amber-500" />
          <span>
            {showPdfView
              ? `Quotation #${createdQuotation?.quotation_number || ""} PDF`
              : "Create Quotation from POS Cart"}
          </span>
        </div>
      }
      maxWidth={showPdfView ? "max-w-4xl" : "max-w-3xl"}
      footer={
        showPdfView ? (
          <div className="flex justify-between items-center w-full">
            <div className="flex items-center gap-2">
              {onClearCart && (
                <Button
                  variant="outline"
                  size="sm"
                  className="text-destructive hover:bg-destructive/10"
                  onClick={() => {
                    onClearCart();
                    toast.success("POS Cart cleared");
                  }}
                >
                  Clear POS Cart
                </Button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowPdfView(false)}
              >
                Back to Quotation
              </Button>
              <Button
                variant="secondary"
                size="sm"
                className="gap-1.5"
                onClick={handlePrintPdf}
              >
                <Printer className="h-4 w-4" /> Print
              </Button>
              <Button size="sm" className="gap-1.5" onClick={handleDownloadPdf}>
                <Download className="h-4 w-4" /> Download PDF
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex justify-between items-center w-full">
            <div className="text-xs text-muted-foreground">
              Total:{" "}
              <span className="font-bold text-foreground text-sm">
                {currencySymbol}
                {totals.total.toFixed(2)}
              </span>{" "}
              ({items.length} items)
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={onHide}>
                Cancel
              </Button>
              <Button
                size="sm"
                variant="secondary"
                disabled={isSubmitting || items.length === 0}
                onClick={() => handleSubmit(false, "draft")}
              >
                {isSubmitting ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                ) : null}
                Save as Draft
              </Button>
              <Button
                size="sm"
                className="gap-1.5 bg-amber-600 hover:bg-amber-700 text-white font-medium shadow"
                disabled={isSubmitting || items.length === 0}
                onClick={() => handleSubmit(true, null)}
              >
                {isSubmitting ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                ) : (
                  <FileDown className="h-4 w-4" />
                )}
                Create &amp; Generate PDF
              </Button>
            </div>
          </div>
        )
      }
    >
      {showPdfView ? (
        <div className="space-y-4 py-2">
          {isGeneratingPdf ? (
            <div className="flex flex-col items-center justify-center py-20 text-muted-foreground gap-3">
              <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
              <p className="text-sm">Generating Quotation PDF document...</p>
            </div>
          ) : pdfBlobUrl ? (
            <div className="w-full h-[520px] rounded-lg border bg-muted/20 overflow-hidden">
              <iframe
                src={`${pdfBlobUrl}#toolbar=0`}
                className="w-full h-full border-0"
                title="Quotation PDF Preview"
              />
            </div>
          ) : (
            <div className="p-8 text-center text-muted-foreground">
              <AlertCircle className="h-10 w-10 text-amber-500 mx-auto mb-2" />
              <p>Unable to preview PDF document.</p>
              <Button
                variant="outline"
                size="sm"
                className="mt-3"
                onClick={() =>
                  createdQuotation && generateQuotationPdf(createdQuotation.id)
                }
              >
                <RefreshCw className="h-4 w-4 mr-2" /> Retry Generation
              </Button>
            </div>
          )}
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center text-muted-foreground gap-3">
          <div className="h-16 w-16 rounded-2xl bg-amber-500/10 flex items-center justify-center text-amber-500">
            <AlertCircle className="h-8 w-8" />
          </div>
          <div>
            <h4 className="font-semibold text-base text-foreground">
              POS Cart is Empty
            </h4>
            <p className="text-xs text-muted-foreground max-w-sm mt-1">
              Add products to the active POS cart first. Quotations are generated
              directly from your selected cart products and customer.
            </p>
          </div>
        </div>
      ) : (
        <div className="space-y-5 py-2">
          {/* Customer & Dates Section */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-3.5 bg-muted/30 rounded-xl border">
            <div>
              <Label className="text-xs text-muted-foreground font-medium">
                Customer
              </Label>
              <div className="font-semibold text-sm mt-1 flex items-center gap-1.5 truncate">
                {customer?.full_name ||
                  customer?.customer_name ||
                  customer?.name ||
                  "Walk-in Customer"}
              </div>
              <p className="text-xs text-muted-foreground truncate">
                {customer?.email || customer?.phone || "No contact info"}
              </p>
            </div>

            <div>
              <Label className="text-xs text-muted-foreground font-medium">
                Quotation Date
              </Label>
              <Input
                type="date"
                value={quotationDate}
                onChange={(e) => setQuotationDate(e.target.value)}
                className="h-8 text-xs mt-1 bg-background"
              />
            </div>

            <div>
              <div className="flex justify-between items-center">
                <Label className="text-xs text-muted-foreground font-medium">
                  Valid Until
                </Label>
                <div className="flex gap-1">
                  <button
                    type="button"
                    onClick={() => handleSetQuickDays(7)}
                    className="text-[10px] text-amber-600 dark:text-amber-400 hover:underline cursor-pointer"
                  >
                    +7d
                  </button>
                  <span className="text-[10px] text-muted-foreground">•</span>
                  <button
                    type="button"
                    onClick={() => handleSetQuickDays(14)}
                    className="text-[10px] text-amber-600 dark:text-amber-400 hover:underline cursor-pointer"
                  >
                    +14d
                  </button>
                  <span className="text-[10px] text-muted-foreground">•</span>
                  <button
                    type="button"
                    onClick={() => handleSetQuickDays(30)}
                    className="text-[10px] text-amber-600 dark:text-amber-400 hover:underline cursor-pointer"
                  >
                    +30d
                  </button>
                </div>
              </div>
              <Input
                type="date"
                value={validUntil}
                onChange={(e) => setValidUntil(e.target.value)}
                className="h-8 text-xs mt-1 bg-background"
              />
            </div>
          </div>

          {/* Items Table */}
          <div className="border rounded-xl overflow-hidden">
            <div className="bg-muted/40 px-3 py-2 text-xs font-semibold flex justify-between items-center border-b">
              <span>Items to Quote ({items.length})</span>
              <span className="text-muted-foreground font-normal">
                Adjust quantities if required
              </span>
            </div>
            <div className="max-h-60 overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow className="text-xs">
                    <TableHead className="w-10">#</TableHead>
                    <TableHead>Product</TableHead>
                    <TableHead className="w-24 text-center">Qty</TableHead>
                    <TableHead className="text-right">Unit Price</TableHead>
                    <TableHead className="text-right">Line Total</TableHead>
                    <TableHead className="w-10"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((item, idx) => (
                    <TableRow key={idx} className="text-xs">
                      <TableCell className="text-muted-foreground">
                        {idx + 1}
                      </TableCell>
                      <TableCell>
                        <div className="font-medium text-foreground truncate max-w-[240px]">
                          {item.name}
                        </div>
                        <div className="flex items-center gap-2">
                          {item.sku && (
                            <span className="text-[10px] text-muted-foreground font-mono">
                              SKU: {item.sku}
                            </span>
                          )}
                          {parseFloat(item.discount_amount || 0) > 0 && (
                            <span className="text-[10px] text-emerald-600 font-medium">
                              • Disc: -{currencySymbol}
                              {parseFloat(item.discount_amount).toFixed(2)}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <Input
                          type="number"
                          min="1"
                          step="1"
                          value={item.quantity}
                          onChange={(e) =>
                            handleUpdateItemQty(idx, e.target.value)
                          }
                          className="h-7 w-16 text-center text-xs mx-auto"
                        />
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {currencySymbol}
                        {parseFloat(item.unit_price || 0).toFixed(2)}
                      </TableCell>
                      <TableCell className="text-right font-mono font-semibold">
                        {currencySymbol}
                        {parseFloat(item.line_total || 0).toFixed(2)}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 text-muted-foreground hover:text-destructive"
                          onClick={() => handleRemoveItem(idx)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>

          {/* Notes and Totals Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-3">
              <div>
                <Label className="text-xs font-medium">Terms &amp; Notes</Label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Terms of quotation, delivery notes, warranty notes..."
                  className="h-20 text-xs mt-1 bg-muted/20"
                />
              </div>

              <div className="flex items-center gap-3">
                <div className="w-1/2">
                  <Label className="text-xs font-medium">Quotation Status</Label>
                  <Select value={status} onValueChange={setStatus}>
                    <SelectTrigger className="h-8 text-xs mt-1">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="sent">Sent to Customer</SelectItem>
                      <SelectItem value="accepted">Accepted</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                      <SelectItem value="expired">Expired</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="w-1/2">
                  <Label className="text-xs font-medium">Overall Discount</Label>
                  <div className="flex gap-1.5 mt-1">
                    <Input
                      type="number"
                      min="0"
                      step="any"
                      value={overallDiscountValue}
                      onChange={(e) =>
                        setOverallDiscountValue(e.target.value)
                      }
                      className="h-8 text-xs w-20"
                    />
                    <Select
                      value={overallDiscountType}
                      onValueChange={setOverallDiscountType}
                    >
                      <SelectTrigger className="h-8 text-xs flex-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="fixed">{currencySymbol}</SelectItem>
                        <SelectItem value="percentage">%</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </div>

            {/* Financials Card */}
            <Card className="bg-muted/20 shadow-none border">
              <CardContent className="p-3.5 space-y-2 text-xs">
                <div className="flex justify-between text-muted-foreground">
                  <span>Subtotal:</span>
                  <span className="font-mono text-foreground font-medium">
                    {currencySymbol}
                    {totals.subtotal.toFixed(2)}
                  </span>
                </div>
                {totals.discount > 0 && (
                  <div className="flex justify-between text-destructive">
                    <span>Discount:</span>
                    <span className="font-mono font-medium">
                      -{currencySymbol}
                      {totals.discount.toFixed(2)}
                    </span>
                  </div>
                )}
                {totals.tax > 0 && (
                  <div className="flex justify-between text-muted-foreground">
                    <span>Tax:</span>
                    <span className="font-mono text-foreground font-medium">
                      +{currencySymbol}
                      {totals.tax.toFixed(2)}
                    </span>
                  </div>
                )}
                <div className="border-t pt-2 mt-2 flex justify-between items-center text-sm font-bold">
                  <span>Quotation Total:</span>
                  <span className="font-mono text-base text-amber-600 dark:text-amber-400">
                    {currencySymbol}
                    {totals.total.toFixed(2)}
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </Modal>
  );
};

PosCreateQuotationModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  currentProducts: PropTypes.array,
  customer: PropTypes.object,
  currencySymbol: PropTypes.string,
  onClearCart: PropTypes.func,
};

export default PosCreateQuotationModal;
